"use client";

import type React from "react";
import {
  Box,
  Button,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Avatar,
  Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot } from "@fortawesome/free-solid-svg-icons";
import {
  agentService,
  type VoiceDetails,
} from "../../voice-services/agentService";
import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";

// Icons for each agent type
const SinglePromptIcon = () => (
  <Box
    as="span"
    w="24px"
    h="24px"
    borderRadius="full"
    color="black"
    bg="gray.100"
    shadow="sm"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="brackets-fill">
        <path
          id="Vector"
          d="M7.75 3.25V4.75H5.5V15.25H7.75V16.75H4V3.25H7.75ZM12.25 3.25H16V16.75H12.25V15.25H14.5V4.75H12.25V3.25Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

const MultiPromptIcon = () => (
  <Box
    as="span"
    w="24px"
    h="24px"
    borderRadius="full"
    color="black"
    bg="gray.100"
    shadow="sm"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="organization-chart">
        <path
          id="Vector"
          d="M12.25 3.25C12.664 3.25 13 3.586 13 4V7C13 7.414 12.664 7.75 12.25 7.75H10.75V9.25H13.75C14.164 9.25 14.5 9.586 14.5 10V12.25H16C16.414 12.25 16.75 12.586 16.75 13V16C16.75 16.414 16.414 16.75 16 16.75H11.5C11.086 16.75 10.75 16.414 10.75 16V13C10.75 12.586 11.086 12.25 11.5 12.25H13V10.75H7V12.25H8.5C8.914 12.25 9.25 12.586 9.25 13V16C9.25 16.414 8.914 16.75 8.5 16.75H4C3.586 16.75 3.25 16.414 3.25 16V13C3.25 12.586 3.586 12.25 4 12.25H5.5V10C5.5 9.586 5.836 9.25 6.25 9.25H9.25V7.75H7.75C7.336 7.75 7 7.414 7 7V4C7 3.586 7.336 3.25 7.75 3.25H12.25ZM7.75 13.75H4.75V15.25H7.75V13.75ZM15.25 13.75H12.25V15.25H15.25V13.75ZM11.5 4.75H8.5V6.25H11.5V4.75Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

const CustomLLMIcon = () => (
  <Box
    as="span"
    w="24px"
    h="24px"
    borderRadius="full"
    color="black"
    bg="gray.100"
    shadow="sm"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
  >
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="links-line">
        <path
          id="Vector"
          d="M10.7949 7.08249L11.8562 8.14374C12.3438 8.63126 12.7305 9.21004 12.9944 9.84703C13.2583 10.484 13.3941 11.1668 13.3941 11.8562C13.3941 12.5457 13.2583 13.2285 12.9944 13.8655C12.7305 14.5024 12.3438 15.0812 11.8562 15.5687L11.5907 15.8335C10.6061 16.8181 9.27066 17.3713 7.8782 17.3713C6.48574 17.3713 5.15031 16.8181 4.1657 15.8335C3.18108 14.8489 2.62793 13.5134 2.62793 12.121C2.62793 10.7285 3.18108 9.39311 4.1657 8.40849L5.22695 9.46974C4.87622 9.81741 4.59763 10.2309 4.40714 10.6865C4.21666 11.1422 4.11805 11.6309 4.11697 12.1248C4.11589 12.6186 4.21236 13.1078 4.40084 13.5642C4.58933 14.0207 4.86611 14.4354 5.21531 14.7846C5.56451 15.1338 5.97924 15.4106 6.43569 15.5991C6.89214 15.7876 7.38133 15.8841 7.87517 15.883C8.36901 15.8819 8.85777 15.7833 9.31339 15.5928C9.76902 15.4023 10.1825 15.1237 10.5302 14.773L10.7957 14.5075C11.4987 13.8043 11.8936 12.8506 11.8936 11.8562C11.8936 10.8619 11.4987 9.90822 10.7957 9.20499L9.73445 8.14374L10.7957 7.08324L10.7949 7.08249ZM15.8334 11.5907L14.7729 10.5302C15.1237 10.1826 15.4023 9.76906 15.5927 9.31344C15.7832 8.85781 15.8818 8.36905 15.8829 7.87521C15.884 7.38138 15.7875 6.89219 15.5991 6.43573C15.4106 5.97928 15.1338 5.56455 14.7846 5.21535C14.4354 4.86616 14.0207 4.58937 13.5642 4.40089C13.1077 4.2124 12.6186 4.11593 12.1247 4.11701C11.6309 4.11809 11.1421 4.21671 10.6865 4.40719C10.2309 4.59767 9.81736 4.87627 9.4697 5.22699L9.2042 5.49249C8.50118 6.19572 8.10625 7.14937 8.10625 8.14374C8.10625 9.13811 8.50118 10.0918 9.2042 10.795L10.2654 11.8562L9.2042 12.9167L8.1437 11.8562C7.65613 11.3687 7.26937 10.7899 7.0055 10.153C6.74163 9.51596 6.60582 8.83323 6.60582 8.14374C6.60582 7.45426 6.74163 6.77152 7.0055 6.13453C7.26937 5.49754 7.65613 4.91876 8.1437 4.43124L8.4092 4.16649C9.39381 3.18187 10.7292 2.62872 12.1217 2.62872C13.5142 2.62872 14.8496 3.18187 15.8342 4.16649C16.8188 5.15111 17.372 6.48653 17.372 7.87899C17.372 9.27145 16.8188 10.6069 15.8342 11.5915L15.8334 11.5907Z"
          fill="currentColor"
        ></path>
      </g>
    </svg>
  </Box>
);

interface CreateAgentProps {
  onAgentCreated?: (agentId?: string) => void;
  buttonProps?: Record<string, any>;
}

export const CreateAgent: React.FC<CreateAgentProps> = ({
  onAgentCreated,
  buttonProps,
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { currentClientId, clients } = useAuth();
  const credits = useStableCredits();

  const [agentName, setAgentName] = useState("");
  const [agentType, setAgentType] = useState<"single" | "multi" | "custom">(
    "single",
  );
  const [selectedVoice, setSelectedVoice] = useState("11labs-Cimo");
  const [voices, setVoices] = useState<VoiceDetails[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isAgentTypeOpen, setIsAgentTypeOpen] = useState(false);
  const [isVoiceOpen, setIsVoiceOpen] = useState(false);

  useEffect(() => {
    const loadVoices = async () => {
      try {
        const voiceList = await agentService.listVoices();
        setVoices(voiceList);
      } catch (error) {
        console.error("Error loading voices:", error);
      }
    };
    loadVoices();
  }, []);

  const handleCreateAgent = async () => {
    if (!agentName.trim()) {
      toast({
        title: "Agent name is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setIsCreating(true);
    try {
      if (agentType === "single") {
        await handleCreateSinglePromptAgent();
      } else if (agentType === "multi") {
        await handleCreateMultiPromptAgent();
      } else if (agentType === "custom") {
        await handleCreateCustomLLM();
      }

      // Reset form and close modal
      setAgentName("");
      setAgentType("single");
      setSelectedVoice("11labs-Cimo");
      onClose();
    } catch {
      // Error handling is done in individual functions
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateSinglePromptAgent = async () => {
    try {
      // ✅ Retell model enum updated
      const llmParams = {
        model: "gpt-4.1-mini" as
          | "gpt-4.1"
          | "gpt-4.1-mini"
          | "gpt-4.1-nano"
          | "gpt-5"
          | "gpt-5.1"
          | "gpt-5.2"
          | "gpt-5-mini"
          | "gpt-5-nano"
          | "claude-4.5-sonnet"
          | "claude-4.5-haiku"
          | "gemini-2.5-flash"
          | "gemini-2.5-flash-lite"
          | "gemini-3.0-flash"
          | null,
        model_temperature: 0.7,
        general_prompt: "",
        start_speaker: "agent" as "agent" | "user",
      };

      const createdLLM = await agentService.createRetellLLM(llmParams);

      const selectedClient = clients.find((c) => c.id === currentClientId);
      const companyName = selectedClient?.company_name;

      const createdAgent = await agentService.create({
        agent_name: agentName,
        response_engine: {
          type: "retell-llm",
          llm_id: createdLLM.llm_id,
        },
        voice_id: selectedVoice,
        language: "en-US",
        ...(companyName && { company_name: companyName }),
      });

      toast({
        title: "Agent created successfully",
        status: "success",
        duration: 3000,
      });

      if (onAgentCreated) {
        onAgentCreated(createdAgent.agent_id);
      }
    } catch (error: any) {
      console.error("Error creating agent:", error);
      toast({
        title: "Error creating agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
      throw error;
    }
  };

  const handleCreateMultiPromptAgent = async () => {
    try {
      // ✅ Start with 1 state so Prompt Tree is usable immediately
      const llmParams = {
        model: "gpt-4.1-mini" as
          | "gpt-4.1"
          | "gpt-4.1-mini"
          | "gpt-4.1-nano"
          | "gpt-5"
          | "gpt-5.1"
          | "gpt-5.2"
          | "gpt-5-mini"
          | "gpt-5-nano"
          | "claude-4.5-sonnet"
          | "claude-4.5-haiku"
          | "gemini-2.5-flash"
          | "gemini-2.5-flash-lite"
          | "gemini-3.0-flash"
          | null,
        model_temperature: 0.7,
        general_prompt: "",
        start_speaker: "agent" as "agent" | "user",
        states: [
          {
            name: "start",
            state_prompt:
              "You are a helpful assistant. Follow the task and stay on track.",
            edges: [],
            tools: [],
          },
        ],
        starting_state: "start",
      };

      const createdLLM = await agentService.createRetellLLM(llmParams);

      const selectedClient = clients.find((c) => c.id === currentClientId);
      const companyName = selectedClient?.company_name;

      const createdAgent = await agentService.create({
        agent_name: agentName,
        response_engine: {
          type: "retell-llm",
          llm_id: createdLLM.llm_id,
        },
        voice_id: selectedVoice,
        language: "en-US",
        ...(companyName && { company_name: companyName }),
      });

      toast({
        title: "Multi-Prompt Agent created successfully",
        status: "success",
        duration: 3000,
      });

      if (onAgentCreated) {
        onAgentCreated(createdAgent.agent_id);
      }
    } catch (error: any) {
      console.error("Error creating Multi-Prompt agent:", error);
      toast({
        title: "Error creating Multi-Prompt agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
      throw error;
    }
  };

  const handleCreateCustomLLM = async () => {
    try {
      const selectedClient = clients.find((c) => c.id === currentClientId);
      const companyName = selectedClient?.company_name;

      // ✅ custom-llm must NOT include llm_id
      const createdAgent = await agentService.create({
        agent_name: agentName,
        response_engine: {
          type: "custom-llm",
          llm_websocket_url: "https://replace-with-your-llm-url.com",
        },
        voice_id: selectedVoice,
        language: "en-US",
        ...(companyName && { company_name: companyName }),
      });

      toast({
        title: "Custom LLM Agent created successfully",
        status: "success",
        duration: 3000,
      });

      if (onAgentCreated) {
        onAgentCreated(createdAgent.agent_id);
      }
    } catch (error: any) {
      console.error("Error creating Custom LLM agent:", error);
      toast({
        title: "Error creating Custom LLM agent",
        description:
          error.response?.data?.message ||
          "An error occurred while creating the agent",
        status: "error",
        duration: 5000,
      });
      throw error;
    }
  };

  const selectedVoiceDetails = voices.find((v) => v.voice_id === selectedVoice);
  const agentTypeLabels = {
    single: "Single-Prompt Agent",
    multi: "Multi-Prompt Agent",
    custom: "Custom LLM Agent",
  };

  const AgentTypeOption = ({
    type,
    icon,
    label,
    description,
  }: {
    type: string;
    icon: React.ReactNode;
    label: string;
    description: string;
  }) => (
    <Tooltip label={description} placement="right" hasArrow>
      <Box
        as="button"
        type="button"
        w="100%"
        px={3}
        py={2}
        display="flex"
        alignItems="center"
        gap={2}
        bg={agentType === type ? "gray.100" : "white"}
        _hover={{ bg: "gray.50" }}
        onClick={() => {
          setAgentType(type as "single" | "multi" | "custom");
          setIsAgentTypeOpen(false);
        }}
        cursor="pointer"
        borderRadius="md"
      >
        {icon}
        <Text color="black" fontSize="sm">
          {label}
        </Text>
      </Box>
    </Tooltip>
  );

  const VoiceOption = ({ voice }: { voice: VoiceDetails }) => (
    <Box
      as="button"
      type="button"
      w="100%"
      px={3}
      py={2}
      display="flex"
      alignItems="center"
      gap={2}
      bg={selectedVoice === voice.voice_id ? "gray.100" : "white"}
      _hover={{ bg: "gray.50" }}
      onClick={() => {
        setSelectedVoice(voice.voice_id);
        setIsVoiceOpen(false);
      }}
      cursor="pointer"
      borderRadius="md"
    >
      <Avatar size="xs" src={voice.avatar_url} name={voice.voice_name} />
      <Text color="black" fontSize="sm">
        {voice.voice_name} ({voice.gender})
      </Text>
    </Box>
  );

  return (
    <>
      <Button
        onClick={onOpen}
        fontWeight="medium"
        leftIcon={<FontAwesomeIcon icon={faRobot} />}
        isDisabled={credits !== null && credits < 0.01}
        title={
          credits !== null && credits < 0.01
            ? "Insufficient balance to create agent"
            : undefined
        }
        {...buttonProps}
      >
        Create Agent
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent bg="white">
          <ModalHeader color="black">Create New Agent</ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel color="black">Agent Name</FormLabel>
              <Input
                placeholder="Enter agent name"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                bg="white"
                borderColor="gray.300"
                color="black"
                _placeholder={{ color: "gray.400" }}
              />
            </FormControl>

            <FormControl mb={4}>
              <FormLabel color="black">Agent Type</FormLabel>
              <Box position="relative">
                <Box
                  borderWidth="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  bg="white"
                  cursor="pointer"
                  onClick={() => setIsAgentTypeOpen(!isAgentTypeOpen)}
                >
                  <Box px={3} py={2}>
                    <HStack spacing={2}>
                      {agentType === "single" && <SinglePromptIcon />}
                      {agentType === "multi" && <MultiPromptIcon />}
                      {agentType === "custom" && <CustomLLMIcon />}
                      <Text color="black" fontSize="sm">
                        {agentTypeLabels[agentType]}
                      </Text>
                    </HStack>
                  </Box>
                </Box>
                {isAgentTypeOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    borderWidth="1px"
                    borderColor="gray.300"
                    borderRadius="md"
                    bg="white"
                    shadow="lg"
                    zIndex={10}
                    p={1}
                  >
                    <AgentTypeOption
                      type="single"
                      icon={<SinglePromptIcon />}
                      label="Single-Prompt Agent"
                      description="For short calls and straightforward tasks"
                    />
                    <AgentTypeOption
                      type="multi"
                      icon={<MultiPromptIcon />}
                      label="Multi-Prompt Agent"
                      description="For lengthy calls and complex tasks"
                    />
                    <AgentTypeOption
                      type="custom"
                      icon={<CustomLLMIcon />}
                      label="Custom LLM Agent"
                      description="Attach your custom llm link"
                    />
                  </Box>
                )}
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel color="black">Agent Voice</FormLabel>
              <Box position="relative">
                <Box
                  borderWidth="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  bg="white"
                  cursor="pointer"
                  onClick={() => setIsVoiceOpen(!isVoiceOpen)}
                >
                  <Box px={3} py={2}>
                    <HStack spacing={2}>
                      {selectedVoiceDetails?.avatar_url && (
                        <Avatar
                          size="xs"
                          src={selectedVoiceDetails.avatar_url}
                          name={selectedVoiceDetails.voice_name}
                        />
                      )}
                      <Text color="black" fontSize="sm">
                        {selectedVoiceDetails?.voice_name || "Select a voice"}{" "}
                        {selectedVoiceDetails?.gender &&
                          `(${selectedVoiceDetails.gender})`}
                      </Text>
                    </HStack>
                  </Box>
                </Box>
                {isVoiceOpen && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    mt={1}
                    borderWidth="1px"
                    borderColor="gray.300"
                    borderRadius="md"
                    bg="white"
                    shadow="lg"
                    zIndex={10}
                    maxH="300px"
                    overflowY="auto"
                    p={1}
                  >
                    {voices.map((voice) => (
                      <VoiceOption key={voice.voice_id} voice={voice} />
                    ))}
                  </Box>
                )}
              </Box>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              onClick={onClose}
              mr={3}
              variant="ghost"
              color="black"
              _hover={{ bg: "gray.100" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAgent}
              colorScheme="primary"
              bg="primary.800"
              _hover={{ bg: "primary.900" }}
              isLoading={isCreating}
              loadingText="Creating..."
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to create agent"
                  : undefined
              }
            >
              Create Agent
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
